if (!customElements.get('quickshop-drawer')) {
  class QuickshopDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = document.querySelector('.quickshop-overlay');
      this.closeButton = this.querySelector('.quickshop-close');
      this.content = this.querySelector('.quickshop-content');
      this.boundOpen = this.open.bind(this);
      this.selectedVariantId = null;
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = null;
      this.submitButtonText = null;
      this.errorMessageWrapper = null;
      this.errorMessage = null;
    }

    connectedCallback() {
      document.querySelectorAll('.quickshop-btn').forEach((btn) => {
        btn.addEventListener('click', this.boundOpen);
      });
      this.overlay.addEventListener('click', this.close.bind(this));
      this.closeButton.addEventListener('click', this.close.bind(this));
    }

    disconnectedCallback() {
      document.querySelectorAll('.quickshop-btn').forEach((btn) => {
        btn.removeEventListener('click', this.boundOpen);
      });
      this.overlay.removeEventListener('click', this.close.bind(this));
      this.closeButton.removeEventListener('click', this.close.bind(this));
      this.variantInputs.forEach((input) => {
        input.removeEventListener('change', this.setSelectedVariant.bind(this));
      });
    }

    open(e) {
      const handle = e.currentTarget.dataset.productHandle;
      this.fetchContent(handle, null);

      e.preventDefault();
      this.classList.add('is-open');
      this.overlay.classList.add('is-open');
      document.body.style.cssText = 'overflow: hidden !important';
    }

    close() {
      this.classList.remove('is-open');
      this.overlay.classList.remove('is-open');
      document.body.style.cssText = 'overflow: auto !important';
    }

    fetchContent(handle, preservedSizes = null) {
      fetch(`/products/${handle}?view=quickshop`)
        .then((res) => res.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const content = doc.querySelector('.quickshop-content-container');

          this.content.innerHTML = content.innerHTML;

          this.variantInputs = this.querySelectorAll('.quickshop-product-option-input');
          this.variantInputs.forEach((input) => {
            input.addEventListener('change', this.setSelectedVariant.bind(this));
          });

          this.submitButton = this.querySelector('.quickshop-add-to-cart');
          if (this.submitButton) {
            this.submitButtonText = this.submitButton.querySelector('span');
            this.submitButton.addEventListener('click', this.addToCart.bind(this));
          }

          this.errorMessageWrapper = this.querySelector('.quickshop-error-message-wrapper');
          this.errorMessage = this.errorMessageWrapper
            ? this.errorMessageWrapper.querySelector('.quickshop-error-message')
            : null;

          const optionsContainer = this.querySelector('.quickshop-options-container');
          const isAlphaSizing = optionsContainer && optionsContainer.dataset.sizing === 'alpha';

          if (isAlphaSizing) {
            const checkedInputs = this.querySelectorAll('.quickshop-product-option-input:checked');
            const selectedSizes = Array.from(checkedInputs)
              .map((input) => input.dataset.size)
              .join(' / ');

            const sizesToIds = this.getSizesToIds();
            if (sizesToIds && sizesToIds[selectedSizes]) {
              this.selectedVariantId = sizesToIds[selectedSizes].id;
            }

            const firstCheckedInput = checkedInputs[0];
            if (firstCheckedInput && firstCheckedInput.dataset.optionIndex) {
              this.recalculateSizeAvailability(firstCheckedInput.dataset.size, firstCheckedInput.dataset.optionIndex);
            }
          } else {
            const initialVariantInput = this.variantInputs[0];
            if (initialVariantInput) {
              this.selectedVariantId = initialVariantInput.value;
            }
          }

          if (preservedSizes && preservedSizes.length > 0) {
            this.restoreSelectedSizes(preservedSizes);
          } else {
            this.selectLocalStorageSizes();
          }

          this.querySelector('product-swatches').updateAvailabilityStyle();
          this.updateAddToCartState();
        });
    }

    getSizesToIds() {
      const script = this.querySelector('.sizes-to-ids');
      if (!script) return null;
      try {
        return JSON.parse(script.innerHTML);
      } catch (error) {
        console.error('Error parsing sizes-to-ids', error);
        return null;
      }
    }

    setSelectedVariant(e) {
      const optionsContainer = this.querySelector('.quickshop-options-container');
      const isAlphaSizing = optionsContainer && optionsContainer.dataset.sizing === 'alpha';

      if (isAlphaSizing) {
        const checkedInputs = this.querySelectorAll('.quickshop-product-option-input:checked');
        const selectedSizes = Array.from(checkedInputs)
          .map((input) => input.dataset.size)
          .join(' / ');

        const sizesToIds = this.getSizesToIds();
        if (sizesToIds && sizesToIds[selectedSizes]) {
          this.selectedVariantId = sizesToIds[selectedSizes].id;
        } else {
          console.warn('No variant found for size combination:', selectedSizes);
        }

        this.recalculateSizeAvailability(e.target.dataset.size, e.target.dataset.optionIndex);
      } else {
        this.selectedVariantId = e.target.value;
      }

      this.saveSelectedSizesToLocalStorage();

      this.querySelector('product-swatches').updateAvailabilityStyle();
      this.updateAddToCartState();
    }

    recalculateSizeAvailability(size, optionIndex) {
      const optionsContainer = this.querySelector('.quickshop-options-container');
      if (!optionsContainer || optionsContainer.dataset.sizing !== 'alpha') return;

      const sizesToIds = this.getSizesToIds();
      if (!sizesToIds) return;

      const fieldsetOption = optionIndex === '2' ? 'Cup Size' : 'Band Size';
      const fieldset = this.querySelector(`fieldset[data-option="${fieldsetOption}"]`);

      if (!fieldset) return;

      fieldset.querySelectorAll('label').forEach((label) => {
        const labelText = label.innerText.trim();
        const combination = optionIndex === '2' ? `${size} / ${labelText}` : `${labelText} / ${size}`;
        const match = sizesToIds[combination];

        if (match && match.available === true) {
          label.classList.remove('quickshop-product-options-value-unavailable');
          const input = fieldset.querySelector(`input[data-size="${labelText}"]`);
          if (input) {
            input.classList.remove('unavailable-input');
          }
        } else {
          label.classList.add('quickshop-product-options-value-unavailable');
          const input = fieldset.querySelector(`input[data-size="${labelText}"]`);
          if (input) {
            input.classList.add('unavailable-input');
          }
        }
      });
    }

    handleErrorMessage(errorMessage = false) {
      if (!this.errorMessageWrapper || !this.errorMessage) return;

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    addToCart(e) {
      e.preventDefault();

      if (!this.selectedVariantId) {
        this.handleErrorMessage('Please select a variant');
        return;
      }

      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage(false);

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
      };

      const formData = new FormData();
      formData.append('id', this.selectedVariantId);
      formData.append('quantity', 1);

      if (this.cart) {
        formData.append(
          'sections',
          this.cart.getSectionsToRender().map((section) => section.id),
        );
        formData.append('sections_url', window.location.pathname);
      }

      config.body = formData;

      fetch(window.routes.cart_add_url || '/cart/add.js', config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description || response.message || 'Error adding item to cart');
            return;
          }

          if (this.cart) {
            this.cart.classList.remove('is-empty');
            if (this.cart.querySelector('.drawer__inner')) {
              this.cart.querySelector('.drawer__inner').classList.remove('is-empty');
            }
            if (this.cart.querySelector('cart-drawer-items')) {
              this.cart.querySelector('cart-drawer-items').classList.remove('is-empty');
            }

            this.cart.renderContents(response);
          } else {
            window.location = window.routes.cart_url || '/cart';
            return;
          }

          this.close();
        })
        .catch((e) => {
          console.error(e);
          this.handleErrorMessage('An error occurred while adding to cart');
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
        });
    }

    restoreSelectedSizes(sizes) {
      sizes.forEach((size) => {
        const sizeInput = this.querySelector(`input[data-size="${size}"]`);

        if (sizeInput && !sizeInput.checked) {
          sizeInput.checked = true;
        }
      });

      if (sizes.length > 0) {
        const lastSize = sizes[sizes.length - 1];
        const lastInput = this.querySelector(`input[data-size="${lastSize}"]`);
        if (lastInput) {
          lastInput.dispatchEvent(new Event('change'));
        }
      }

      this.updateAddToCartState();
    }

    saveSelectedSizesToLocalStorage() {
      try {
        const sizes = this.querySelectorAll('.quickshop-product-option-input:checked');
        if (sizes && sizes.length > 0) {
          const sizesString = Array.from(sizes)
            .map((size) => size?.dataset?.size || size?.value || '')
            .filter((value) => value.trim() !== '')
            .join(', ');

          if (sizesString) {
            if (sizes.length > 1) {
              localStorage.setItem('braCupSize', sizesString);
            } else if (sizes.length === 1) {
              localStorage.setItem('generalSize', sizesString);
            }
          }
        }
      } catch (error) {
        console.error('Error saving size selection to localStorage:', error);
      }
    }

    selectLocalStorageSizes() {
      const script = this.querySelector('.js-quickshop-product-options-io');

      if (!script) return;

      const jsonScript = JSON.parse(script.innerHTML);

      const optionsContainer = this.querySelector('.quickshop-options-container');
      const sizeType = optionsContainer.dataset.sizing;

      const localStorageVals =
        sizeType === 'alpha' ? localStorage.getItem('braCupSize') : localStorage.getItem('generalSize');

      if (!localStorageVals) return;

      const variantForSize = jsonScript.variants.find((variant) => variant.title === localStorageVals);

      if (!variantForSize) return;

      const sizeArr = localStorageVals.split(', ');

      sizeArr.forEach((size) => {
        const sizeInput = this.querySelector(`input[data-size="${size}"]`);

        if (sizeInput && !sizeInput.checked) {
          sizeInput.checked = true;
        }
      });

      if (sizeArr.length > 0) {
        const lastSize = sizeArr[sizeArr.length - 1];
        const lastInput = this.querySelector(`input[data-size="${lastSize}"]`);
        if (lastInput) {
          lastInput.dispatchEvent(new Event('change'));
        }
      }

      this.updateAddToCartState();
    }

    updateAddToCartState() {
      const hasUnavailableInputChecked = this.querySelector('.unavailable-input:checked');

      if (hasUnavailableInputChecked) {
        this.submitButton.setAttribute('disabled', true);
      } else {
        this.submitButton.removeAttribute('disabled');
      }
    }
  }

  customElements.define('quickshop-drawer', QuickshopDrawer);
}

if (!customElements.get('quickshop-image-gallery')) {
  class QuickshopImageGallery extends HTMLElement {
    constructor() {
      super();
      this.swiperEl = this.querySelector('.quickshop-gallery-swiper');
    }

    connectedCallback() {
      this.initializeSwiper();
    }

    disconnectedCallback() {
      this.swiper.destroy();
    }

    initializeSwiper() {
      this.swiper = new Swiper(this.swiperEl, {
        slidesPerView: 1.8,
        spaceBetween: 8,
        loop: true,
      });
    }
  }

  customElements.define('quickshop-image-gallery', QuickshopImageGallery);
}